import * as React from 'react';
import { Handles, Rail as BaseRail, Slider, SliderItem, Tracks } from 'react-compound-slider';
import { FilterInterface } from '../../Filter/FilterInterface';
import { RangeFilterValueType } from '../../Filter/RangeFilter';
import { RangeFilterValue } from '../../Filter/RangeFilterValue';
import { Handle, PrepareLabelCallback } from '../RangeSlider/Handle';
import { Rail } from '../RangeSlider/Rail';
import { Scaling } from '../RangeSlider/Scaling';
import { Track } from '../RangeSlider/Track';
import { OptionsType } from './AbstractFilterView';
import { FilterEvent } from './FilterEvent';
import { MinMaxRangeFilter } from './MinMaxRangeFilter';
import { AbstractRangeFilterView, RangeFilterProps } from './Range/AbstractRangeFilterView';

export interface RangeSliderFilterProps<VALUE_TYPE, OPTION_TYPE> extends RangeFilterProps<VALUE_TYPE, OPTION_TYPE> {
    logarithmic?: boolean,
    prepareHandleLabel?: PrepareLabelCallback
}

export class RangeSliderFilter<VALUE_TYPE extends RangeFilterValueType,
    OPTION_TYPE = number,
    P extends RangeSliderFilterProps<VALUE_TYPE, OPTION_TYPE> = RangeSliderFilterProps<VALUE_TYPE, OPTION_TYPE>,
    >
    extends AbstractRangeFilterView<VALUE_TYPE, OPTION_TYPE, P> {
    constructor(props: Readonly<P>) {
        super(props);
        this.handleUpdate = this.handleUpdate.bind(this);
    }

    protected renderInputInner(
        props: P,
        filter: FilterInterface<VALUE_TYPE>,
        options: OptionsType<OPTION_TYPE>,
        bounds: RangeFilterValue,
        currentValue: RangeFilterValue
    ): JSX.Element | null {
        const currentMinValue = this.mapValueToPosition(currentValue.min);
        const currentMaxValue = this.mapValueToPosition(currentValue.max);
        const availableMin = bounds.min;
        const availableMax = bounds.max;
        if (availableMin === availableMax) {
            console.error(`[RangeSliderFilter] Filter min must be smaller than max`, availableMin, availableMax);

            if (props.showErrors) {
                return <div className="pf-error">
                    Filter min must be smaller than max <small>({availableMin}, {availableMax})</small>
                </div>;
            } else {
                return <MinMaxRangeFilter<VALUE_TYPE, OPTION_TYPE>
                    onChange={props.onChange}
                    filter={filter}
                    options={options}/>;
            }
        }

        const domain = props.logarithmic ? [0, this.getLogarithmicUpperBound(options)] : [availableMin, availableMax];
        const className = 'pf-range-slider ' + (filter.isActive ? '-active' : '-inactive');

        return <div className="pf-range-slider-wrapper">
            <Slider
                className={className}
                step={1}
                mode={2}
                domain={domain}
                values={[currentMinValue, currentMaxValue]}
                onChange={this.handleUpdate}
            >
                <BaseRail>{railProps => <Rail {...railProps}/>}</BaseRail>
                <Handles>
                    {({handles, getHandleProps}) => (
                        <div className="slider-handles">
                            {handles.map(handle => (
                                <Handle
                                    label={this.getHandleLabel(handle)}
                                    key={handle.id}
                                    handle={handle}
                                    getHandleProps={getHandleProps}
                                />
                            ))}
                        </div>
                    )}
                </Handles>
                <Tracks left={false} right={false}>
                    {({tracks, getTrackProps}) => (
                        <div className="slider-tracks">
                            {tracks.map(({id, source, target}) => (
                                <Track
                                    key={id}
                                    source={source}
                                    target={target}
                                    getTrackProps={getTrackProps}
                                />
                            ))}
                        </div>
                    )}
                </Tracks>
            </Slider>
        </div>;
    }

    private getLogarithmicUpperBound(options: OptionsType<OPTION_TYPE>) {
        return 4 * (options.length as number);
    }

    private handleUpdate(newValues: readonly number[]) {
        if (!this.didInitialize) {
            this.setDidInitialize();
            return;
        }

        const newMin = newValues[0];
        const newMax = newValues[1];
        if (!(isNaN(newMin) || isNaN(newMax))) {
            const value = this.buildNewFilterValue(newMin, newMax);

            this.handleChange(new FilterEvent(undefined, value));
        }
    }

    private buildNewFilterValue(newMin: number, newMax: number): RangeFilterValue {
        if (this.props.logarithmic) {
            const newMinLog = this.mapPositionToValue(newMin);
            const newMaxLog = this.mapPositionToValue(newMax);

            console.info(
                `[RangeSliderFilter] Handle update: ${newMin}=${newMinLog} ${newMax}=${newMaxLog}`,
                newMin,
                newMinLog,
                newMax,
                newMaxLog
            );

            return new RangeFilterValue(newMinLog, newMaxLog);
        } else {
            console.info(
                `[RangeSliderFilter] Handle update: ${newMin} ${newMax}`,
                newMin,
                newMax
            );

            return new RangeFilterValue(newMin, newMax);
        }
    }

    private mapPositionToValue(position: number) {
        const props = this.props;
        if (!props.logarithmic) {
            return position;
        } else {
            return this.getScalingHelper(props).mapPositionToValue(position);
        }
    }

    private mapValueToPosition(value: number): number {
        const props = this.props;
        if (!props.logarithmic) {
            return value;
        } else {
            return this.getScalingHelper(props).mapValueToPosition(value);
        }
    }

    private getScalingHelper(props: RangeSliderFilterProps<VALUE_TYPE, OPTION_TYPE>): Scaling {
        const options = props.options;
        const bounds = this.getOptionsBounds(options);

        return new Scaling(bounds, this.getLogarithmicUpperBound(options));
    }

    private getHandleLabel(handle: SliderItem): string {
        const preparedValue = this.mapPositionToValue(handle.value);

        const prepareHandleLabel = this.props.prepareHandleLabel;
        if (prepareHandleLabel) {
            return prepareHandleLabel(preparedValue);
        } else {
            return preparedValue.toLocaleString();
        }
    }
}
